import { inject } from '@angular/core';
import { asyncScheduler, filter, from, observeOn, switchMap, take } from 'rxjs';

import { MultiGuard } from '@plugins/client/routing/multi-guard.creator';

import { TranslateService } from '../services/translate.service';

const verify = <T>(
  importStatement: () => Promise<T>,
  service: TranslateService,
) => {
  return from(importStatement()).pipe(
    observeOn(asyncScheduler),
    switchMap(() => {
      return service.languageLoaded$.pipe(
        filter((isReady) => isReady),
        take(1),
      );
    }),
  );
};

/** Standalone translation guard function. Supply an arrow function that
 * resolves to the target module/component which has the translation
 * loader decorator applied or imports a custom translation module. */
export const createTranslationGuard =
  <T>(importStatement: () => Promise<T>) =>
  () =>
    verify(importStatement, inject(TranslateService));

/** For use with `queueGuards` helper. */
export const createTranslationGuardClass =
  <T>(importStatement: () => Promise<T>) =>
  () =>
    new TranslationGuardClass(importStatement);

/** For use in `queueGuards` helper. Its usage is identical
 * to the `createTranslationGuard` function. */
class TranslationGuardClass extends MultiGuard {
  protected service = inject(TranslateService);
  constructor(protected importStatement: () => Promise<unknown>) {
    super();
  }

  protected canPass() {
    return verify(this.importStatement, this.service);
  }
}
